// ASRS Part A scoring logic (pure functions — no React dependencies).
//
// The ASRS v1.1 splits its 18 items into two groups with different thresholds
// for what counts as a "positive" response.  For the WLC RTC screener we only
// use the first 6 items (Part A).  Within those 6 items:
//
//   Group A (items 1, 2, 3): "Sometimes", "Often", or "Very Often" = +1
//   Group B (items 4, 5, 6): only "Often" or "Very Often"          = +1
//
// A Part A score of >= 4 out of 6 is the standard screening threshold that
// suggests further ADHD assessment is warranted.

const GROUP_A_QUESTIONS = [1, 2, 3];
const GROUP_B_QUESTIONS = [4, 5, 6];

export const ASRS_THRESHOLD = 4;

/**
 * Calculate the ASRS Part A score from an array of answer objects.
 *
 * @param {Array<{questionId: string, answer: string}>} answers
 *   Each entry must have a `questionId` like 'asrs_1' and an `answer` string
 *   matching one of the five ASRS response labels.
 * @returns {number} Score in the range 0-6.
 */
export function calculateASRSPartAScore(answers) {
    let score = 0;

    for (const { questionId, answer } of answers) {
        if (!questionId || !questionId.startsWith('asrs_')) continue;

        const questionNum = parseInt(questionId.replace('asrs_', ''), 10);
        if (Number.isNaN(questionNum)) continue;

        if (GROUP_A_QUESTIONS.includes(questionNum)) {
            if (['Sometimes', 'Often', 'Very Often'].includes(answer)) {
                score += 1;
            }
        } else if (GROUP_B_QUESTIONS.includes(questionNum)) {
            if (['Often', 'Very Often'].includes(answer)) {
                score += 1;
            }
        }
    }

    return score;
}

/**
 * Returns true when the Part A score meets or exceeds the screening threshold.
 */
export function meetsASRSThreshold(score) {
    return score >= ASRS_THRESHOLD;
}

/**
 * After a page refresh, break / below-threshold screens are not persisted.
 * If the user has finished all ASRS answers but has not yet moved past the
 * gate (still on the last ASRS index, no post-ASRS answers), recompute which
 * virtual screen they should see.
 *
 * @param {Array<{questionIndex: number, answer: *}>} submittedAnswers
 * @param {Array<{id: string, phase: string}>} allQuestions
 * @param {number} currentQuestionIndex
 * @param {number} asrsCount - Usually ASRS_COUNT (6)
 * @returns {'break' | 'below_threshold' | null}
 */
export function deriveAsrsGateScreen(submittedAnswers, allQuestions, currentQuestionIndex, asrsCount) {
    if (!Array.isArray(submittedAnswers) || !Array.isArray(allQuestions)) return null;
    // Already past the gate (impairment onwards) — do not re-show it.
    if (currentQuestionIndex >= asrsCount) return null;

    const asrsForScoring = submittedAnswers
        .filter((a) => {
            const q = allQuestions[a.questionIndex];
            return q && q.phase === 'asrs';
        })
        .map((a) => ({
            questionId: allQuestions[a.questionIndex].id,
            answer: a.answer,
        }));

    if (asrsForScoring.length < asrsCount) return null;
    // Still parked on the last ASRS question after answering all six.
    if (currentQuestionIndex !== asrsCount - 1) return null;

    const score = calculateASRSPartAScore(asrsForScoring);
    return meetsASRSThreshold(score) ? 'break' : 'below_threshold';
}
